import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap'
import { formatExportServiceOptions } from '../../../api/export/formatters'
import exportApi from '../../../api/export/hooks'
import { MODAL_TYPES } from '../../../common/constants'
import { hideModal } from '../../../store/modal'
import { resetTableCheckbox } from '../../../store/table'
import { ExportAdsModalProps, SelectedItems } from './types'

const ExportAdsModal = (props: ExportAdsModalProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { isOpen, exportAdsModal } = props
    const { propertiesIds } = exportAdsModal || { propertiesIds: [] }

    const [selectedItems, setSelectedItems] = useState<SelectedItems>({ portals: {}, websites: {} })

    const { data: portalsData } = exportApi.GetPortals.useQuery()
    const { data: websitesData } = exportApi.GetWebsites.useQuery()
    const { mutate: handleExport, isPending, data, error } = exportApi.ExportAds.useMutation()

    const portals = useMemo(() => formatExportServiceOptions(portalsData), [portalsData])
    const websites = useMemo(() => formatExportServiceOptions(websitesData), [websitesData])

    const isDataFetched = data && data.length > 0

    const requestPayload = {
        properties: propertiesIds,
        portals: Object.keys(selectedItems.portals).filter((key) => selectedItems.portals[key]),
        websites: Object.keys(selectedItems.websites).filter((key) => selectedItems.websites[key]),
    }

    const allPortalsSelected = useMemo(() => {
        const selectedPortalsValues = Object.values(selectedItems.portals)

        return selectedPortalsValues.length === portals.length && selectedPortalsValues.every(Boolean)
    }, [selectedItems.portals, portals.length])

    const isSelected = useMemo(() => Object.values(selectedItems).some((obj) => Object.values(obj).some(Boolean)), [selectedItems])

    const handleSelectAllPortals = () => {
        const portalsState: Record<string, boolean> = {}

        portals.forEach((portal) => (portalsState[portal.value] = !allPortalsSelected))

        setSelectedItems((prev) => ({
            ...prev,
            portals: portalsState,
        }))
    }

    const handleCheckbox = (key: keyof SelectedItems, selectedValue: string) => {
        setSelectedItems((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [selectedValue]: !prev[key][selectedValue],
            },
        }))
    }

    const handleClose = () => {
        const modalPayload = {
            modalType: MODAL_TYPES.EXPORT_ADS_MODAL,
        }

        dispatch(hideModal(modalPayload))
        dispatch(resetTableCheckbox(true))
    }

    const FormContent = () => (
        <>
            <Row className="mb-4">
                <h6>{t('form.property.tab.export.portals')}</h6>
                <div>
                    <Input
                        type="checkbox"
                        className="form-check-input"
                        id="select-all-portals"
                        onChange={handleSelectAllPortals}
                        checked={allPortalsSelected}
                    />
                    <Label className="form-check-label ms-2" for="select-all-portals">
                        {t('form.property.tab.export.checkboxAll')}
                    </Label>
                </div>

                {portals.map((portal) => (
                    <Col lg={6} key={portal.value}>
                        <Input
                            type="checkbox"
                            className="form-check-input"
                            id={portal.value}
                            onChange={() => handleCheckbox('portals', portal.value)}
                            checked={!!selectedItems.portals[portal.value]}
                        />
                        <Label className="form-check-label ms-2" for={portal.value}>
                            {portal.label}
                        </Label>
                    </Col>
                ))}
            </Row>
            <Row className="mb-4">
                <h6>{t('app.common.web')}</h6>
                <div>
                    {websites.map((website) => (
                        <div key={website.value}>
                            <Input
                                type="checkbox"
                                className="form-check-input"
                                id={website.value}
                                onChange={() => handleCheckbox('websites', website.value)}
                                checked={!!selectedItems.websites[website.value]}
                            />
                            <Label className="form-check-label ms-2" for={website.value}>
                                {website.label}
                            </Label>
                        </div>
                    ))}
                </div>
            </Row>
            <div className="d-flex justify-content-end">
                <Button
                    onClick={() => handleExport(requestPayload)}
                    className="btn-label align-self-end"
                    type="submit"
                    color="success"
                    disabled={!isSelected || isPending}
                >
                    {isPending ? (
                        <i className="mdi mdi-spin mdi-loading label-icon align-middle fs-16 me-2"></i>
                    ) : (
                        <i className="mdi mdi-export label-icon align-middle fs-16 me-2"></i>
                    )}

                    {t('form.property.tab.export')}
                </Button>
            </div>
        </>
    )

    return (
        <Modal isOpen={isOpen} centered size={isDataFetched ? 'lg' : ''}>
            <ModalHeader toggle={handleClose}>
                <p className="m-0">{t('app.common.exportPortalAds')}</p>
            </ModalHeader>
            <hr className="mx-3 mb-1" />
            <ModalBody>
                {!isDataFetched && !error && <FormContent />}
                {isDataFetched && !error && <div className="text-primary text-center">{t('modal.exportAds.message')}</div>}
                {error && <div className="text-danger text-center">{error?.message}</div>}
            </ModalBody>
        </Modal>
    )
}

export default ExportAdsModal
